import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { Button, Card, LoadingOverlay, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import DataView from './components/DataView';
import { ResidueType } from '../../../../models/core/residues.type';
import { Filter, FilterData } from './components/Filter';
import PageContent from '../../../../components/core/PageContent/PageContent';
import residuesService from '../../../../services/core/residues.service';
import { Feature, SessionStorageKey } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

function ResidueSearch() {
  const navigate = useNavigate();

  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    const cacheResult = JSON.parse(
      sessionStorage.getItem(SessionStorageKey.ResidueSearch) || '[]'
    ) as ResidueType[];

    if (cacheResult.length > 0) {
      setData(cacheResult);
    } else {
      filter({ residuo: '', codigoIBAMA: '' });
    }
  }, []);

  const clear = () => {
    sessionStorage.removeItem(SessionStorageKey.ResidueSearch);
    setData([]);
  };

  const filter = async (inputFilterData: FilterData) => {
    try {
      setLoading(true);
      const result = await residuesService.search({
        residuo: inputFilterData?.residuo || null,
        codigoIBAMA: inputFilterData?.codigoIBAMA || null,
        limit: 1000,
        offset: 0,
      });
      sessionStorage.setItem(SessionStorageKey.ResidueSearch, JSON.stringify(result));
      setData(result);
    } catch (error: any) {
      showNotification({
        title: 'Resíduos',
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar resíduos.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmActionResult = async (item: ResidueType, action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'excluir':
          await residuesService.delete({ idResiduo: item.idResiduo });
          setData(data.filter((x) => x.idResiduo !== item.idResiduo));
          break;
        case 'visualizar':
          navigate(`/residues/${item.idResiduo}`, {
            state: {
              residue: item,
            },
          });
          return;
        case 'editar':
          navigate(`/residues/${item.idResiduo}/edit`, {
            state: {
              residue: item,
            },
          });
          return;
        default:
          break;
      }

      showNotification({
        title: 'Resíduos',
        message: `Resíduo excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Resíduos',
        message: error?.isBusinessException ? error.description : `Não foi possível ${action} o resíduo.`,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Residue}
        title="Resíduos"
        description="Gerencie resíduos."
        buttons={[
          <Button
            key="Adicionar"
            color="primary"
            leftSection={<IconPlus size={18} />}
            disabled={loading}
            onClick={() => navigate('/residues/add')}
            data-permission={Permission.ResidueAdd}
          >
            Adicionar
          </Button>,
        ]}
      />
      <PageContent>
        <Filter filter={filter} clear={clear} loading={loading} />
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Paper shadow="xs" p="md" withBorder>
            <DataView data={data} confirmActionResult={confirmActionResult} />
          </Paper>
        </div>
      </PageContent>
    </Card>
  );
}

export default ResidueSearch;
